import Phaser from 'phaser';
import { t } from '@i18n/index';
import { PALETTE, TEXT_COLOUR, titleStyle, readoutStyle, FONT, px, motionMs } from '../theme';
import { layoutOf, watchResize } from '../ui/layout';
import { createScrollArea } from '../ui/scrollArea';
import { createButton } from '../ui/button';
import { showAdviser } from '../ui/adviserPanel';
import { portraitKey, addBackdrop, MENU_BACKDROP } from '../assets';
import { drawFallbackPortrait } from '../render/portrait';
import type { Campaign } from '@core/campaign/campaign';
import { audio } from '../audio/engine';
import { DRONE_ROOT } from '../audio/motifs';

/**
 * Der Einstieg in einen neuen Durchlauf.
 *
 * Zwischen Editor und Station steht ein Bildschirm, der die Figur einmal
 * ausschreibt: Gesicht, Name, Herkunft und der Verlust, der sie hierher
 * gebracht hat. Bis M6 begann das Spiel damit, dass man ohne Vorgeschichte an
 * einer Station stand und eine Liste sah — die Figur war Statistik.
 *
 * Danach begruesst Dr. Roskopf. Sie hat zu jeder Herkunft etwas anderes zu
 * sagen, und sie sagt es knapp: Wer hier ankommt, hat schon einmal jemanden
 * verloren, und niemand im Institut haelt dazu eine Rede.
 */
export class IntroScene extends Phaser.Scene {
  private campaign!: Campaign;

  constructor() {
    super('Intro');
  }

  create(): void {
    this.campaign = this.registry.get('campaign') as Campaign;
    this.cameras.main.setBackgroundColor(PALETTE.abyss);

    const layout = layoutOf(this);
    const { width, height, centreX } = layout;
    const character = this.campaign.character;

    // Das Wrack aus dem Menue, noch weiter abgedunkelt.
    addBackdrop(this, MENU_BACKDROP, 0.3);

    /*
     * Portraet neben dem Text, wenn Platz ist — sonst darueber, kleiner.
     * Die Akte liegt in einem Scrollbereich; auf einem Telefon im Querformat
     * reichte die Hoehe sonst nicht bis zum Verlust.
     */
    const stacked = layout.compact;
    const portraitWidth = stacked ? 104 : 176;
    const portraitHeight = stacked ? 130 : 220;
    const columnGap = 40;
    const textWidth = stacked
      ? Math.min(420, width - layout.margin * 2)
      : Math.min(360, width - (centreX + 108) - 40);
    const contentWidth = stacked ? textWidth : portraitWidth + columnGap + textWidth;
    const portraitLeft = stacked ? (contentWidth - portraitWidth) / 2 : 0;
    const textLeft = stacked ? 0 : portraitWidth + columnGap;
    const textTop = stacked ? portraitHeight + 16 : 4;

    const title = this.add.text(centreX, 24, t('intro.title'), titleStyle(22)).setOrigin(0.5, 0);
    const top = title.y + title.height + 18;
    const footer = 44 + 36;
    const before = new Set(this.children.list);

    this.add
      .image(portraitLeft, 0, this.portraitTexture(character.portraitId))
      .setOrigin(0, 0)
      .setDisplaySize(portraitWidth, portraitHeight);

    const name = this.add.text(textLeft, textTop, character.name, {
      fontFamily: FONT.display,
      fontSize: px(26),
      color: PALETTE.ink,
      wordWrap: { width: textWidth },
    });

    const role = this.add.text(textLeft, name.y + name.height + 6, t(`background.${character.background}`), {
      fontFamily: FONT.mono,
      fontSize: px(12),
      color: TEXT_COLOUR.base,
    });

    // Die Herkunft in eigenen Worten — je Hintergrund eine eigene Fassung,
    // in der dritten Person, damit sie wie eine Akte klingt und nicht wie ein
    // Tagebuch.
    const blurb = this.add.text(textLeft, role.y + role.height + 12, t(`intro.${character.background}`), {
      ...readoutStyle(12),
      wordWrap: { width: textWidth },
      lineSpacing: 4,
    });

    // Der Verlust setzt unter dem Absatz auf, nicht auf einer festen Zeile:
    // Die drei Herkunftstexte sind verschieden lang, und in der laengsten
    // Fassung stiessen die beiden sonst aneinander.
    const dividerY = blurb.y + blurb.height + 18;
    this.add.rectangle(textLeft, dividerY, 48, 1, PALETTE.panelEdge).setOrigin(0, 0);

    this.add.text(
      textLeft,
      dividerY + 16,
      t('intro.lost', {
        name: character.lostName,
        relation: t(character.lostRelationKey),
      }),
      {
        ...readoutStyle(12),
        color: PALETTE.inkDim,
        fontStyle: 'italic',
        wordWrap: { width: textWidth },
        lineSpacing: 3,
      },
    );

    const created = this.children.list.filter((child) => !before.has(child));
    const scroll = createScrollArea(this, {
      x: centreX - contentWidth / 2,
      y: top,
      width: contentWidth,
      height: height - top - footer,
    });
    scroll.content.add(created);
    scroll.finish();

    const begin = (): void => {
      this.scene.start('WorldMap');
    };
    createButton(this, centreX, height - 40, t('intro.begin'), begin, { width: 220 });
    this.input.keyboard?.on('keydown', (event: KeyboardEvent) => {
      if (event.key === 'Enter') begin();
    });
    watchResize(this, () => this.scene.restart());

    this.cameras.main.fadeIn(motionMs(600), 5, 8, 13);

    audio.startDrone(DRONE_ROOT.base);
    audio.setTension(0.2);

    // Roskopf kommt einen Moment spaeter — erst soll man die eigene Figur
    // ansehen koennen, dann meldet sich das Institut.
    this.time.delayedCall(900, () => {
      showAdviser(this, {
        // Roskopf greift die Entscheidung aus dem Prolog auf — sie war der
        // erste Einsatz, und die Station weiss davon.
        text: [
          t(`adviser.welcome.${character.background}`),
          ...this.campaign.toll.entries
            .filter((entry) => entry.incidentId === 'prologue')
            .map((entry) => t(entry.tier === 'rescue' ? 'adviser.prologue.rescue' : 'adviser.prologue.wreck')),
        ].join('\n\n'),
        dismissKey: 'adviser.welcome.ack',
      });
    });
  }

  private portraitTexture(id: string): string {
    const key = portraitKey(id);
    if (this.textures.exists(key)) return key;

    const fallback = `portrait-fallback:${id}`;
    if (!this.textures.exists(fallback)) {
      this.textures.addCanvas(fallback, drawFallbackPortrait(id, 128));
    }
    return fallback;
  }
}
